import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { GATE_AFTER_DETAIL_PHOTOS, GATE_AFTER_SCROLLED_PHOTOS } from '../data/site';

const EnquiryContext = createContext(null);

const STORE_KEY = 'pj_enquiry_submitted_v1';

const readSubmitted = () => {
  try {
    return window.localStorage.getItem(STORE_KEY) === '1';
  } catch {
    return false;
  }
};

export function EnquiryProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(readSubmitted);
  const [interest, setInterest] = useState('');
  const [reason, setReason] = useState('gate');

  /**
   * Two counters, because the two journeys deserve different thresholds:
   *
   *  - `detail`  photos opened in the full-screen viewer. The prompt appears on
   *              the 3rd one, which is the moment of real interest.
   *  - `scroll`  thumbnails that merely scrolled past in the grid. A desktop
   *              grid puts three on screen at once, so gating on 3 here would
   *              fire the modal before the visitor had done anything. This
   *              counter only acts as a long backstop.
   */
  const detailSeen = useRef(new Set());
  const scrollSeen = useRef(new Set());
  const extra = useRef(0); // pushed up each time the visitor dismisses

  const resetViews = useCallback(() => {
    detailSeen.current = new Set();
    scrollSeen.current = new Set();
  }, []);

  const fire = useCallback((contextLabel) => {
    setInterest(contextLabel);
    setReason('gate');
    setOpen(true);
  }, []);

  /**
   * @param id           unique photo id
   * @param contextLabel collection name, prefilled into the WhatsApp message
   * @param source       'detail' (viewer) or 'scroll' (grid thumbnail)
   */
  const registerView = useCallback(
    (id, contextLabel = '', source = 'scroll') => {
      if (submitted) return;

      if (source === 'detail') {
        if (detailSeen.current.has(id)) return;
        detailSeen.current.add(id);
        if (detailSeen.current.size > GATE_AFTER_DETAIL_PHOTOS + extra.current) {
          fire(contextLabel);
        }
        return;
      }

      if (scrollSeen.current.has(id)) return;
      scrollSeen.current.add(id);
      if (scrollSeen.current.size > GATE_AFTER_SCROLLED_PHOTOS + extra.current) {
        fire(contextLabel);
      }
    },
    [submitted, fire]
  );

  /** Open the form directly from a button. */
  const openEnquiry = useCallback((contextLabel = '', why = 'manual') => {
    setInterest(contextLabel);
    setReason(why);
    setOpen(true);
  }, []);

  /** Dismissed without submitting — give them room before asking again. */
  const dismiss = useCallback(() => {
    setOpen(false);
    extra.current += 4;
  }, []);

  const markSubmitted = useCallback(() => {
    setSubmitted(true);
    setOpen(false);
    try {
      window.localStorage.setItem(STORE_KEY, '1');
    } catch {
      /* private mode — the in-memory flag still holds for this session */
    }
  }, []);

  const value = useMemo(
    () => ({ open, submitted, interest, reason, registerView, resetViews, openEnquiry, dismiss, markSubmitted }),
    [open, submitted, interest, reason, registerView, resetViews, openEnquiry, dismiss, markSubmitted]
  );

  return <EnquiryContext.Provider value={value}>{children}</EnquiryContext.Provider>;
}

export const useEnquiry = () => {
  const ctx = useContext(EnquiryContext);
  if (!ctx) throw new Error('useEnquiry must be used inside <EnquiryProvider>');
  return ctx;
};
